import React, { useState, useRef } from 'react';
import {X, Calculator, RotateCcw, Download, Circle} from 'lucide-react';
import html2canvas from 'html2canvas';

interface CaudalCircularCalculatorProps {
  onClose: () => void;
}

interface Results {
  gasto: string;
  ahid: string;
  pmojado: string;
  rhid: string;
  eagua: string;
  velocidad: string;
  nfroud: string;
  energia: string;
  tflujo: string;
}

const emptyResults: Results = {
  gasto: '',
  ahid: '',
  pmojado: '',
  rhid: '',
  eagua: '',
  velocidad: '',
  nfroud: '',
  energia: '',
  tflujo: ''
};

const CaudalCircularCalculator: React.FC<CaudalCircularCalculatorProps> = ({ onClose }) => {
  const [tirante, setTirante] = useState('');
  const [diametro, setDiametro] = useState('');
  const [rugosidad, setRugosidad] = useState('');
  const [pendiente, setPendiente] = useState('');
  const [results, setResults] = useState<Results>(emptyResults);
  const [error, setError] = useState('');
  const exportRef = useRef<HTMLDivElement>(null);

  const calcular = () => {
    setError('');
    const tir = Number(tirante);
    const d = Number(diametro);
    const n = Number(rugosidad);
    const s = Number(pendiente);

    if (tir <= 0 || d <= 0 || n <= 0 || s <= 0 || n > 1) {
      setError('Datos faltantes o irreales. Verifique que todos los valores sean positivos y n ≤ 1.');
      setResults(emptyResults);
      return;
    }
    if (tir > d) {
      setError('El tirante no puede ser mayor que el diámetro.');
      setResults(emptyResults);
      return;
    }

    const theta = Math.atan(s);
    const so = Math.sin(theta);
    const angulo = Math.acos(1 - (2 * tir) / d);
    const an = 0.25 * (angulo - 0.5 * Math.sin(2 * angulo)) * Math.pow(d, 2);
    const pn = angulo * d;
    const rh = an / pn;
    const asl = d * Math.sin(angulo);
    const v = (1 / n) * Math.pow(rh, 2 / 3) * Math.pow(so, 0.5);
    const q = v * an;
    const fr = v / Math.pow((9.81 * an) / asl, 0.5);
    const ener = tir + Math.pow(v, 2) / (2 * 9.81);

    const trunc = (x: number) => (Math.trunc(x * 10000) / 10000).toString();

    setResults({
      gasto: trunc(q),
      ahid: trunc(an),
      pmojado: trunc(pn),
      rhid: trunc(rh),
      eagua: trunc(asl),
      velocidad: trunc(v),
      nfroud: trunc(fr),
      energia: trunc(ener),
      tflujo: fr > 1 ? 'Supercrítico' : 'Subcrítico'
    });
  };

  const limpiar = () => {
    setTirante('');
    setDiametro('');
    setRugosidad('');
    setPendiente('');
    setResults(emptyResults);
    setError('');
  };

  const exportarPNG = async () => {
    if (!exportRef.current) return;
    try {
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `caudal-circular-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al exportar PNG:', err);
    }
  };

  const hasResults = results.gasto !== '';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-4 relative">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl px-6 py-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Circle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Caudal Circular</h2>
                <p className="text-blue-100 text-sm">Diseño hidráulico de canales circulares</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Description */}
          <div className="px-6 pt-6">
            <p className="text-gray-600 text-sm bg-blue-50 border border-blue-100 rounded-lg p-4">
              Debe ingresar los datos para poder realizar el diseño hidráulico de canales circulares.
            </p>
          </div>

          {/* Content (exportable) */}
          <div ref={exportRef} className="p-6 bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Inputs */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5">
                <div className="text-center mb-4">
                  <h3 className="text-amber-700 font-bold text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                    ─── Datos ───
                  </h3>
                </div>

                {/* Diagrama dinámico de la sección circular */}
                <CircularChannelDiagram
                  tirante={Number(tirante) || 0}
                  diametro={Number(diametro) || 0}
                />

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Yn = Tirante Normal (m):
                    </label>
                    <input
                      type="number"
                      value={tirante}
                      onChange={(e) => setTirante(e.target.value)}
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      D = Diámetro (m):
                    </label>
                    <input
                      type="number"
                      value={diametro}
                      onChange={(e) => setDiametro(e.target.value)}
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      n = Rugosidad (Adim):
                    </label>
                    <input
                      type="number"
                      value={rugosidad}
                      onChange={(e) => setRugosidad(e.target.value)}
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="0.013"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      So = Pendiente (Adim):
                    </label>
                    <input
                      type="number"
                      value={pendiente}
                      onChange={(e) => setPendiente(e.target.value)}
                      min="0"
                      step="any"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="0.001"
                    />
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5">
                <div className="text-center mb-4">
                  <h3 className="text-amber-700 font-bold text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                    ─── Resultados ───
                  </h3>
                </div>
                <div className="space-y-2.5 text-sm">
                  {[
                    { label: 'Gasto (m³/s)', value: results.gasto },
                    { label: 'Área Hidráulica (m²)', value: results.ahid },
                    { label: 'Perímetro Mojado (m)', value: results.pmojado },
                    { label: 'Radio Hidráulico (m)', value: results.rhid },
                    { label: 'Espejo del Agua (m)', value: results.eagua },
                    { label: 'Velocidad (m/s)', value: results.velocidad },
                    { label: 'Número de Froude (Adim.)', value: results.nfroud },
                    { label: 'Energía Específica (m)', value: results.energia },
                    { label: 'Tipo de flujo', value: results.tflujo }
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 border border-blue-100">
                      <span className="text-gray-700 font-medium">{row.label}:</span>
                      <span className={`font-mono font-semibold ${
                        row.label === 'Tipo de flujo' && row.value
                          ? row.value === 'Supercrítico' ? 'text-red-600' : 'text-green-600'
                          : 'text-blue-700'
                      }`}>
                        {row.value || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
                ⚠️ {error}
              </div>
            )}

            {/* Footer brand (visible in PNG export) */}
            {hasResults && (
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-400">
                  Generado por <span className="font-bold text-blue-600">openHH</span> · {new Date().toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={calcular}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-5 rounded-xl shadow-md transition-all"
            >
              <Calculator className="w-5 h-5" />
              Calcular
            </button>
            <button
              onClick={limpiar}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-5 rounded-xl transition-all"
            >
              <RotateCcw className="w-5 h-5" />
              Limpiar
            </button>
            <button
              onClick={exportarPNG}
              disabled={!hasResults}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 px-5 rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-5 h-5" />
              Exportar PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Diagrama SVG dinámico de la sección transversal circular del canal.
 * Refleja en tiempo real D (diámetro) e Yn (tirante normal) ingresados.
 * Muestra el agua llenando la tubería, cotas con flechas y el porcentaje de llenado.
 */
const CircularChannelDiagram: React.FC<{ tirante: number; diametro: number }> = ({
  tirante,
  diametro,
}) => {
  const W = 360;
  const H = 220;
  const cx = W / 2;
  const cy = H / 2 - 5;

  // Radio de dibujo (mantiene el círculo dentro del SVG)
  const R = 70;

  const d = diametro > 0 ? diametro : 1;
  // Limitar tirante al diámetro para no romper el dibujo
  const yClamped = Math.max(0, Math.min(tirante, d));
  const fillRatio = yClamped / d; // 0..1

  // Altura del agua dentro del círculo (en píxeles)
  const waterHeightPx = fillRatio * 2 * R;
  // Y de la superficie del agua (desde top del círculo)
  const waterTopY = cy + R - waterHeightPx;

  // Half-width del espejo del agua (cuerda en la altura waterTopY)
  // distance from center: (cy - waterTopY)
  const distFromCenter = cy - waterTopY;
  const halfChord =
    Math.abs(distFromCenter) <= R
      ? Math.sqrt(Math.max(0, R * R - distFromCenter * distFromCenter))
      : 0;

  const fmt = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');
  const pctLlenado = fillRatio * 100;

  // Estado del llenado
  const estado =
    diametro <= 0
      ? 'Sin diámetro'
      : tirante <= 0
      ? 'Vacío'
      : tirante >= diametro
      ? 'Lleno'
      : tirante / diametro > 0.5
      ? 'Mayor a la mitad'
      : 'Menor a la mitad';

  // Path para el área de agua:
  // El agua se acumula desde el FONDO (cy+R) hacia arriba hasta waterTopY.
  // El path traza: desde xL,yTop → línea horizontal → xR,yTop
  // luego ARCO por la parte INFERIOR del círculo (sweep=1, large-arc según llenado)
  // de vuelta a xL,yTop. Se clippa al círculo para no salirse.
  const waterPath =
    fillRatio <= 0
      ? ''
      : fillRatio >= 1
      ? // círculo completo: dos semicírculos
        `M ${cx - R},${cy} A ${R},${R} 0 1 1 ${cx + R},${cy} A ${R},${R} 0 1 1 ${cx - R},${cy} Z`
      : (() => {
          const xL = cx - halfChord;
          const xR = cx + halfChord;
          const yTop = waterTopY;
          // large-arc=1 si llenado > 50% (el arco inferior es mayor a 180°)
          const largeArc = fillRatio > 0.5 ? 1 : 0;
          // Desde xL,yTop línea recta a xR,yTop (superficie del agua)
          // luego arco horario (sweep=1) por la parte de ABAJO hasta xL,yTop
          return `M ${xL},${yTop} L ${xR},${yTop} A ${R},${R} 0 ${largeArc} 1 ${xL},${yTop} Z`;
        })();

  return (
    <div className="mb-4 bg-white rounded-lg border border-amber-300 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-3 py-2 bg-amber-100/60 border-b border-amber-200">
        <span className="text-xs font-semibold text-amber-900">Sección transversal circular</span>
        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white text-amber-700 border border-amber-300">
          {estado} · {fmt(pctLlenado)}%
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="circWaterGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0891b2" stopOpacity="0.95" />
          </linearGradient>
          <radialGradient id="pipeGrad" cx="50%" cy="50%" r="50%">
            <stop offset="85%" stopColor="#f3f4f6" />
            <stop offset="100%" stopColor="#9ca3af" />
          </radialGradient>
          <pattern id="pipeWall" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#9ca3af" strokeWidth="1.2" opacity="0.5" />
          </pattern>
          {/* ClipPath al círculo interior para que el agua nunca salga del tubo */}
          <clipPath id="circClip">
            <circle cx={cx} cy={cy} r={R - 1} />
          </clipPath>
        </defs>

        {/* Pared exterior de la tubería */}
        <circle cx={cx} cy={cy} r={R + 8} fill="url(#pipeWall)" stroke="#6b7280" strokeWidth="1" />
        {/* Interior de la tubería */}
        <circle cx={cx} cy={cy} r={R} fill="#fefce8" stroke="#374151" strokeWidth="2" />

        {/* Agua — recortada al círculo interior */}
        {waterPath && (
          <g clipPath="url(#circClip)">
            <path d={waterPath} fill="url(#circWaterGrad)" stroke="none" />
            {/* Onditas en la superficie (solo si no está lleno y hay cuerda) */}
            {fillRatio < 1 && halfChord > 6 && (
              <path
                d={`M ${cx - halfChord},${waterTopY} Q ${cx - halfChord / 2},${waterTopY - 3} ${cx},${waterTopY} T ${cx + halfChord},${waterTopY}`}
                fill="none"
                stroke="#ffffff"
                strokeWidth="1"
                opacity="0.7"
              />
            )}
          </g>
        )}
        {/* Borde interior del tubo encima del agua */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#374151" strokeWidth="2" />

        {/* Cota del diámetro D - flecha horizontal arriba */}
        <g>
          <line
            x1={cx - R}
            y1={cy - R - 18}
            x2={cx + R}
            y2={cy - R - 18}
            stroke="#1d4ed8"
            strokeWidth="1.2"
          />
          <polygon
            points={`${cx - R},${cy - R - 18} ${cx - R + 6},${cy - R - 22} ${cx - R + 6},${cy - R - 14}`}
            fill="#1d4ed8"
          />
          <polygon
            points={`${cx + R},${cy - R - 18} ${cx + R - 6},${cy - R - 22} ${cx + R - 6},${cy - R - 14}`}
            fill="#1d4ed8"
          />
          {/* Líneas de extensión */}
          <line x1={cx - R} y1={cy - R} x2={cx - R} y2={cy - R - 22} stroke="#1d4ed8" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <line x1={cx + R} y1={cy - R} x2={cx + R} y2={cy - R - 22} stroke="#1d4ed8" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <text
            x={cx}
            y={cy - R - 24}
            textAnchor="middle"
            fill="#1e40af"
            fontSize="11"
            fontWeight="700"
          >
            D = {fmt(diametro)} m
          </text>
        </g>

        {/* Cota del tirante Yn - flecha vertical lateral derecha */}
        <g>
          <line
            x1={cx + R + 22}
            y1={waterTopY}
            x2={cx + R + 22}
            y2={cy + R}
            stroke="#dc2626"
            strokeWidth="1.2"
          />
          {tirante > 0 && (
            <>
              <polygon
                points={`${cx + R + 22},${waterTopY} ${cx + R + 18},${waterTopY + 6} ${cx + R + 26},${waterTopY + 6}`}
                fill="#dc2626"
              />
              <polygon
                points={`${cx + R + 22},${cy + R} ${cx + R + 18},${cy + R - 6} ${cx + R + 26},${cy + R - 6}`}
                fill="#dc2626"
              />
            </>
          )}
          {/* Líneas de extensión */}
          <line x1={cx + halfChord} y1={waterTopY} x2={cx + R + 26} y2={waterTopY} stroke="#dc2626" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <line x1={cx} y1={cy + R} x2={cx + R + 26} y2={cy + R} stroke="#dc2626" strokeWidth="0.8" strokeDasharray="2 2" opacity="0.6" />
          <text
            x={cx + R + 30}
            y={(waterTopY + cy + R) / 2 + 4}
            fill="#b91c1c"
            fontSize="11"
            fontWeight="700"
          >
            Yn = {fmt(tirante)} m
          </text>
        </g>

        {/* Línea de fondo (referencia) */}
        <line
          x1={cx - R - 4}
          y1={cy + R}
          x2={cx + R + 4}
          y2={cy + R}
          stroke="#374151"
          strokeWidth="1"
        />

        {/* Centro del círculo (marca cruz pequeña) */}
        <line x1={cx - 3} y1={cy} x2={cx + 3} y2={cy} stroke="#6b7280" strokeWidth="0.8" />
        <line x1={cx} y1={cy - 3} x2={cx} y2={cy + 3} stroke="#6b7280" strokeWidth="0.8" />
      </svg>
    </div>
  );
};

export default CaudalCircularCalculator;
